import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Package, ArrowRight, Loader2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Carousel Component for products with multiple images
const ProductImageCarousel = ({ product, altText, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Combine main image with additional images
  const allImages = [product.image_url, ...(product.image_urls || [])].filter(Boolean);
  const hasMultipleImages = allImages.length > 1;
  
  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };
  
  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };
  
  return (
    <div className="aspect-[4/3] overflow-hidden relative group/carousel">
      <img
        src={allImages[currentIndex]}
        alt={altText}
        className="w-full h-full object-cover img-zoom cursor-pointer"
        loading="lazy"
        onClick={() => onImageClick(allImages, currentIndex)}
      />
      
      {/* Navigation arrows - only show if multiple images */}
      {hasMultipleImages && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur shadow-md flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5 text-charcoal" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur shadow-md flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5 text-charcoal" />
          </button>
          
          {/* Image indicators */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {allImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/60 hover:bg-white/80'}`}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Lightbox Component for expanded image view
const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
        aria-label="Close lightbox"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      
      {/* Main image */}
      <div className="relative max-w-5xl max-h-[85vh] mx-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
      </div>
      
      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur px-4 py-2 rounded-full">
          <span className="text-white text-sm font-medium">{currentIndex + 1} / {images.length}</span>
        </div>
      )}
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto p-2">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all ${idx === currentIndex ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-75'}`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [homepageCategories, setHomepageCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { images: [], index: 0 }
  const { language, t } = useLanguage();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const [productsResponse, homepageResponse] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/settings/homepage`),
      ]);
      setProducts(productsResponse.data);
      setHomepageCategories(homepageResponse.data?.equipment_categories || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const categoryLabelMap = homepageCategories.reduce((acc, category) => {
    if (!category?.key) return acc;
    acc[category.key] = language === "es"
      ? (category.title_es || category.title_en || category.key)
      : (category.title_en || category.title_es || category.key);
    return acc;
  }, {});

  const productCategoryKeys = new Set(products.map((p) => p.category).filter(Boolean));

  const categories = [
    { id: "all", label: t.products.allCategories },
    ...homepageCategories
      .filter((category) => category?.key && productCategoryKeys.has(category.key))
      .map((category) => ({
        id: category.key,
        label: categoryLabelMap[category.key] || category.key.replace(/_/g, " "),
      })),
  ];

  const filteredProducts =
    activeCategory === "all"
      ? products
      : products.filter((p) => p.category === activeCategory);

  const getCategoryLabel = (category) => {
    return categoryLabelMap[category] || category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get translated product name - fallback to English if Spanish is empty
  const getProductName = (product) => {
    if (language === "es" && product.name_es) {
      return product.name_es;
    }
    return product.name;
  };

  // Get translated product description - fallback to English if Spanish is empty
  const getProductDescription = (product) => {
    if (language === "es" && product.description_es) {
      return product.description_es;
    }
    return product.description;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <div className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-charcoal mb-4">
            {t.products.title}
          </h1>
          <p className="text-lg text-charcoal/70 max-w-2xl">
            {t.products.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="category-filters">
          {categories.map((category) => (
            <button
              key={category.id}
              data-testid={`filter-${category.id}`}
              onClick={() => setActiveCategory(category.id)}
              className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                activeCategory === category.id
                  ? "bg-sage text-white"
                  : "bg-cream text-charcoal/70 hover:bg-sage/10"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-charcoal/30 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-charcoal mb-2">
              No Products Found
            </h3>
            <p className="text-charcoal/60">
              Check back soon for new inventory.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => {
              // Generate bilingual alt text: [English Name] | [Spanish Name] or just name if only one exists
              const altText = product.name_es && product.name !== product.name_es
                ? `${product.name} | ${product.name_es}`
                : product.name;
              
              // Check if product has multiple images
              const allImages = [product.image_url, ...(product.image_urls || [])].filter(Boolean);
              const hasMultipleImages = allImages.length > 1;
              
              return (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className={`group bg-white rounded-3xl border border-charcoal/10 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 overflow-hidden animate-fade-in`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative">
                    <ProductImageCarousel 
                      product={product} 
                      altText={altText}
                      onImageClick={(images, idx) => setLightbox({ images, index: idx })}
                    />
                    <div className="absolute top-4 left-4 z-10">
                      <span className="bg-white/90 backdrop-blur text-charcoal text-sm font-medium px-3 py-1.5 rounded-full">
                        {getCategoryLabel(product.category)}
                      </span>
                    </div>
                    {hasMultipleImages && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="bg-black/60 backdrop-blur text-white text-xs font-medium px-2 py-1 rounded-full">
                          {allImages.length} photos
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-charcoal mb-2">
                      {getProductName(product)}
                    </h3>
                    <p className="text-charcoal/60 text-sm mb-4 line-clamp-2">
                      {getProductDescription(product)}
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-heading font-bold text-sage">
                          ${product.price_per_day}
                        </span>
                        <span className="text-charcoal/50 text-sm">{t.products.perDay}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-charcoal/50">
                          {product.total_inventory} {t.products.available}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-cream rounded-3xl p-12">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-charcoal mb-4">
            {t.cta.title}
          </h2>
          <p className="text-charcoal/60 mb-6 max-w-xl mx-auto">
            {t.cta.subtitle}
          </p>
          <Link to="/booking">
            <Button
              data-testid="go-to-booking-btn"
              className="bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-6 text-lg font-bold btn-hover"
            >
              {t.cta.button}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Image Lightbox */}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default Products;
